import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid, Cell, ReferenceLine,
} from "recharts";
import { TrendingUp, DollarSign, Receipt, Landmark, Wallet, Package } from "lucide-react";

const fmtMonth = (m) => { const [y, mo] = (m || "").split("-"); return mo ? `${mo}/${y.slice(2)}` : m; };
const AXIS = { stroke: "#71717a", fontSize: 11 };
const TIP = { background: "#121217", border: "1px solid rgba(212,175,55,0.3)", borderRadius: 8, color: "#fff" };
const kAxis = (v) => (Math.abs(v) >= 1000 ? `R$${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k` : `R$${Math.round(v)}`);

export default function FinancialEvolution() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/financial-evolution").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  const series = (data?.series || []).map((d) => ({ ...d, label: fmtMonth(d.month) }));
  const totals = (data?.series || []).reduce((a, d) => ({
    revenue: a.revenue + d.revenue_total, profit: a.profit + d.gross_profit,
    expenses: a.expenses + d.expenses, net: a.net + d.net, tax: a.tax + d.tax_reserve,
  }), { revenue: 0, profit: 0, expenses: 0, net: 0, tax: 0 });

  return (
    <div className="space-y-6" data-testid="financial-evolution-page">
      <div>
        <h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Evolução Financeira</h1>
        <p className="text-zinc-400 mt-1">Desempenho mês a mês dos últimos 12 meses.</p>
      </div>

      <DailyTracking />

      {!data ? (
        <div className="alpha-card p-10 text-center text-zinc-500" data-testid="fin-loading">Carregando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="fin-kpis">
            <Kpi icon={TrendingUp} label="Faturamento (12m)" value={money(totals.revenue)} />
            <Kpi icon={DollarSign} label="Lucro bruto (12m)" value={money(totals.profit)} tone="green" />
            <Kpi icon={Receipt} label="Despesas (12m)" value={money(totals.expenses)} tone="red" />
            <Kpi icon={Wallet} label="Líquido (12m)" value={money(totals.net)} tone={totals.net < 0 ? "red" : "green"} />
            <Kpi icon={Landmark} label="Impostos reservados (12m)" value={money(totals.tax)} tone="amber" />
          </div>

          <div className="alpha-card p-5" data-testid="fin-main-chart">
            <div className="text-white font-semibold mb-4">Faturamento, Despesas, Lucro e Líquido</div>
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="label" {...AXIS} />
                <YAxis {...AXIS} tickFormatter={kAxis} />
                <Tooltip formatter={(v) => money(v)} contentStyle={TIP} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenue_total" name="Faturamento" fill="#D4AF37" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="expenses" name="Despesas" fill="#EF4444" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Line type="monotone" dataKey="gross_profit" name="Lucro bruto" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="net" name="Líquido" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="alpha-card p-5" data-testid="fin-origin-chart">
              <div className="text-white font-semibold mb-4">Faturamento por origem</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" {...AXIS} />
                  <YAxis {...AXIS} tickFormatter={kAxis} />
                  <Tooltip formatter={(v) => money(v)} contentStyle={TIP} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue_products" name="Produtos" stackId="a" fill="#D4AF37" maxBarSize={28} />
                  <Bar dataKey="revenue_services" name="Serviços" stackId="a" fill="#3B82F6" maxBarSize={28} />
                  <Bar dataKey="revenue_devices" name="Aparelhos" stackId="a" fill="#10B981" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="alpha-card p-5" data-testid="fin-tax-chart">
              <div className="text-white font-semibold mb-4">Impostos reservados</div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="label" {...AXIS} />
                  <YAxis {...AXIS} tickFormatter={kAxis} />
                  <Tooltip formatter={(v) => money(v)} contentStyle={TIP} />
                  <Bar dataKey="tax_reserve" name="Impostos" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={28}>
                    {series.map((e) => <Cell key={e.month} fill="#F59E0B" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="alpha-card p-5" data-testid="fin-table">
            <div className="text-white font-semibold mb-4">Detalhamento mensal</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-zinc-500 text-xs uppercase tracking-wider text-left border-b border-white/10">
                    <th className="py-2 pr-4">Mês</th>
                    <th className="py-2 pr-4 text-right">Faturamento</th>
                    <th className="py-2 pr-4 text-right">Produtos</th>
                    <th className="py-2 pr-4 text-right">Serviços</th>
                    <th className="py-2 pr-4 text-right">Aparelhos</th>
                    <th className="py-2 pr-4 text-right">Lucro bruto</th>
                    <th className="py-2 pr-4 text-right">Despesas</th>
                    <th className="py-2 pr-4 text-right">Líquido</th>
                    <th className="py-2 text-right">Impostos</th>
                  </tr>
                </thead>
                <tbody>
                  {[...series].reverse().map((h) => (
                    <tr key={h.month} data-testid={`fin-row-${h.month}`} className="border-b border-white/5">
                      <td className="py-2 pr-4 text-zinc-200">{fmtMonth(h.month)}</td>
                      <td className="py-2 pr-4 text-right text-gold-bright">{money(h.revenue_total)}</td>
                      <td className="py-2 pr-4 text-right text-zinc-400">{money(h.revenue_products)}</td>
                      <td className="py-2 pr-4 text-right text-zinc-400">{money(h.revenue_services)}</td>
                      <td className="py-2 pr-4 text-right text-zinc-400">{money(h.revenue_devices)}</td>
                      <td className="py-2 pr-4 text-right text-emerald-400">{money(h.gross_profit)}</td>
                      <td className="py-2 pr-4 text-right text-red-400">{money(h.expenses)}</td>
                      <td className={`py-2 pr-4 text-right ${h.net < 0 ? "text-red-400" : "text-blue-400"}`}>{money(h.net)}</td>
                      <td className="py-2 text-right text-amber-400">{money(h.tax_reserve)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DailyTracking() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("settings.manage");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [goal, setGoal] = useState("");
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/reports/daily?month=${month}`).then((r) => { setData(r.data); setGoal(r.data.goal ? String(r.data.goal) : ""); }).catch(() => setData(null));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const daysInMonth = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  const g = data?.goal || 0;
  const dailyGoal = g > 0 ? g / daysInMonth : 0;
  const totalRev = data?.total_revenue || 0;
  const remaining = Math.max(0, g - totalRev);
  const today = new Date();
  const isCurrent = month === today.toISOString().slice(0, 7);
  const dayOfMonth = isCurrent ? today.getDate() : daysInMonth;
  const daysLeft = Math.max(0, daysInMonth - dayOfMonth);
  const neededPerDay = daysLeft > 0 ? remaining / daysLeft : 0;
  const pct = data?.progress_percent;
  const saveGoal = async () => {
    setSaving(true);
    try { await api.put("/settings", { monthly_revenue_goal: parseFloat(goal) || 0 }); toast.success("Meta salva"); load(); }
    catch { toast.error("Não foi possível salvar a meta."); } finally { setSaving(false); }
  };
  const chart = (data?.series || []).map((d) => ({ name: d.date.slice(8, 10), revenue: d.revenue }));
  return (
    <div className="alpha-card p-5 space-y-4" data-testid="daily-tracking-panel">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-white font-semibold">Acompanhamento diário · {fmtMonth(month)}</div>
          <div className="text-xs text-zinc-500 mt-1">Faturamento por dia e progresso da meta do mês (vendas financiadas só entram após confirmar o repasse).</div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs text-zinc-400 mb-1.5 block">Mês</Label>
            <input data-testid="daily-month-select" type="month" value={month} onChange={(e) => setMonth(e.target.value || month)} className="h-10 rounded-md bg-[#0A0A0C] border border-white/10 text-zinc-200 px-3 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400 mb-1.5 block">Meta de faturamento (R$)</Label>
            <Input data-testid="daily-goal-input" type="number" min="0" value={goal} onChange={(e) => setGoal(e.target.value)} disabled={!canManage} className="i h-10 w-40" placeholder="0,00" />
          </div>
          {canManage && <Button data-testid="daily-goal-save" onClick={saveGoal} disabled={saving} className="h-10 text-black font-semibold" style={{ background: "linear-gradient(135deg,#F3C64F,#D4AF37 55%,#AA820A)" }}>Salvar</Button>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Faturamento no mês" value={money(totalRev)} />
        <Kpi icon={Landmark} label="Meta" value={g > 0 ? money(g) : "—"} tone="amber" />
        <Kpi icon={Receipt} label="Falta para a meta" value={g > 0 ? money(remaining) : "—"} />
        <Kpi icon={Package} label="Média/dia necessária" value={g > 0 && daysLeft > 0 ? money(neededPerDay) : "—"} tone="green" />
      </div>

      {g > 0 && (
        <div data-testid="daily-goal-progress">
          <div className="flex justify-between text-xs text-zinc-400 mb-1">
            <span>Progresso da meta</span>
            <span className="text-gold-bright font-semibold">{pct != null ? `${pct}%` : "—"}</span>
          </div>
          <div className="h-3 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, pct || 0)}%`, background: "linear-gradient(90deg,#F3C64F,#D4AF37 55%,#AA820A)" }} />
          </div>
          {isCurrent && <div className="text-[11px] text-zinc-500 mt-1">{daysLeft} dia(s) restante(s) no mês</div>}
        </div>
      )}

      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={chart}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
          <XAxis dataKey="name" stroke="#71717a" fontSize={11} />
          <YAxis stroke="#71717a" fontSize={11} tickFormatter={kAxis} />
          <Tooltip formatter={(v) => money(v)} labelFormatter={(l) => `Dia ${l}`} contentStyle={TIP} />
          {dailyGoal > 0 && <ReferenceLine y={dailyGoal} stroke="#F59E0B" strokeDasharray="4 4" label={{ value: "Meta/dia", fill: "#F59E0B", fontSize: 10, position: "insideTopRight" }} />}
          <Bar dataKey="revenue" radius={[6, 6, 0, 0]} fill="#D4AF37" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, tone }) => {
  const color = tone === "green" ? "text-emerald-400" : tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : "text-white";
  const iconColor = tone === "green" ? "text-emerald-400" : tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : "text-gold-bright";
  return (
    <div className="alpha-card p-5">
      <Icon size={18} className={iconColor} />
      <div className={`text-xl font-mono-kpi font-extrabold mt-2 ${color}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{label}</div>
    </div>
  );
};
